import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useEnvs } from '@next-box/envs';
import { useEffect, useState } from 'react';
import { FeaturePlayer } from '#components/FeaturePlayer/index.tsx';
import { CollectionDrawer } from '#components/FeaturePlayerModal/Movie/CollectionDrawer.tsx';
import { formatCollection } from '#components/FeaturePlayerModal/Movie/helpers/formatCollection.ts';
import { getNextMovie, getPrevMovie } from '#components/FeaturePlayerModal/Movie/helpers/getNextPrevMovie.ts';
import { type MovieCollectionData } from '#components/FeaturePlayerModal/Movie/types.ts';
import { PlayerNavigation } from '#components/PlayerSelectorTabs/FeaturePlayerTabPanel/PlayerNavigation.tsx';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';

export type FeaturePlayerMovieTabPanelProps = {
  id: string;
  title?: string;
};

const GET_MOVIE_COLLECTION = "\nquery GetMovieCollection(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieCollection\n  }\n}\n\nfragment MovieCollection on Movie {\n  belongsToCollection {\n    name\n    parts {\n      backdropPath\n      posterPath\n      releaseDate\n      runtime\n      status\n      tagline\n      title\n      voteAverage\n      voteCount\n    }\n  }\n}\n";

export const FeaturePlayerMovieTabPanel = ({ id, title }: FeaturePlayerMovieTabPanelProps) => {
  const { getEnv } = useEnvs();
  const { config } = useToolbelt();
  const addRecentlyWatchedMovie = useStore(store => store.addRecentlyWatchedMovie);
  const [collectionDrawOpen, setCollectionDrawOpen] = useState(false);
  const [partId, setPartId] = useState<string | number>(id);
  const baseUrl = getEnv('NEXT_PUBLIC_EXOTIC_PINK_PTARMIGAN_PROVIDER') ?? '';

  const { execute, loading, result } = useQuery<MovieCollectionData>(GET_MOVIE_COLLECTION, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'FeaturePlayerMovieTabPanel' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const { name } = result?.data.movie.belongsToCollection ?? {};
  const items = formatCollection(result?.data, config);
  const hasCollection = !loading && items.length > 0;
  const currentMovie = items.find(item => item.id === partId);
  const nextMovieId = getNextMovie(items, partId);
  const prevMovieId = getPrevMovie(items, partId);

  return (
    <>
      <FeaturePlayer
        height="40%"
        onClick={() => {
          // @ts-expect-error TODO: Need to refactor so partId is string
          addRecentlyWatchedMovie(partId);
        }}
        responsiveHeights={{
          md: `40%`,
          sm: '56.25%',
          xs: '75%',
        }}
        url={`${atob(baseUrl)}/movie/${String(partId)}`}
      />
      {hasCollection ? (
        <PlayerNavigation
          hasNext={!!nextMovieId}
          hasPrev={!!prevMovieId}
          onNext={() => {
            if (nextMovieId) {
              setPartId(nextMovieId);
            }
          }}
          onOpenPlaylist={() => {
            setCollectionDrawOpen(true);
          }}
          onPrev={() => {
            if (prevMovieId) {
              setPartId(prevMovieId);
            }
          }}
          playlist={hasCollection}
        >
          {currentMovie?.title ?? title}
        </PlayerNavigation>
      ) : undefined}
      {hasCollection ? (
        <CollectionDrawer
          items={items}
          name={name}
          onPartClick={part => {
            setPartId(part);
            setCollectionDrawOpen(false);
          }}
          open={collectionDrawOpen}
          selectedPartId={partId}
          setOpen={setCollectionDrawOpen}
        />
      ) : undefined}
    </>
  );
};
